import { Box, Dialog, Flex, Tabs, Image, Center, Portal } from '@chakra-ui/react';
import { BackIcon, SearchInput } from '@halo/ui-chat-react';
import { FullBox } from '@halo/ui-react';
import { useState, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { DerivedWallet, CoinState, Net } from '@halo/wallet-app';
import { AllNetType, ChainType } from '@halo/wallet-app';
import { useWalletStore } from '@/wallet';
import { usePortalContainer } from '@/scripts/hooks';
import AllNetworksImg from './images/all-net.png';
import { CoinItem } from './coin';

const initDefaultNet = {
  id: AllNetType.ID,
  chainid: 1,
  alias: AllNetType.CHAIN_TYPE,
  chainName: AllNetType.CHAIN_TYPE,
  chainType: AllNetType.CHAIN_TYPE,
  node: [{ t: '', u: '' }],
};
export interface SelectTokenDialogProps {
  value?: CoinState;
  currentAccount: DerivedWallet;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  setSelectedToken: (token: CoinState) => void;
  onClose?: () => void;
}

export function SelectTokenDialog({
  value,
  currentAccount,
  isOpen,
  setIsOpen,
  setSelectedToken,
}: SelectTokenDialogProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(isOpen);
  const [allNetCoins, setAllNetCoins] = useState<CoinState[]>([]);
  const [currentNet, setCurrent] = useState(initDefaultNet as Net);

  const [searchValue, setSearchValue] = useState('');
  const [isMoreOpen, setIsMoreOpen] = useState(false);

  const { portalContainerRef } = usePortalContainer();

  const tabs = [
    {
      label: t('transfer.token'),
      value: 'Token',
    },
    // {
    //   label: 'NFT',
    //   value: 'NFT',
    // },
  ];

  const { getAccountNetCoins, getAccountSupportedNetList } = useWalletStore();
  const supportNetList = useMemo(() => {
    return getAccountSupportedNetList(currentAccount, true);
  }, [currentAccount, getAccountSupportedNetList]);

  const coinsSupportNetList = useMemo(() => {
    return isMoreOpen || !open
      ? supportNetList.filter((net) => net.id === initDefaultNet.id)
      : supportNetList.filter(
          (net) =>
            net.id !== initDefaultNet.id && (net.isMainNet || net.chainType === ChainType.WK)
        );
  }, [isMoreOpen, open, supportNetList]);

  const currentNetCoins = useMemo(() => {
    return currentNet?.id === initDefaultNet.id || searchValue
      ? allNetCoins
      : allNetCoins.filter((coin) => coin.netId === currentNet.id);
  }, [allNetCoins, currentNet.id, searchValue]);

  useEffect(() => {
    if (currentAccount && coinsSupportNetList) {
      (async () => {
        const allNetCoins = coinsSupportNetList.map(async (net) => {
          const coins = await getAccountNetCoins(currentAccount.id, net.id).catch(() => []);
          return coins as CoinState[];
        });
        const coinses = await Promise.all(allNetCoins);
        setAllNetCoins(coinses.flat() || []);
      })();
    }
  }, [currentAccount, getAccountNetCoins, coinsSupportNetList]);

  useEffect(() => {
    if (currentNetCoins) {
      const currentNetCoin = currentNetCoins.find((coin: CoinState & { type?: string }) =>
        value?.id ? coin.id === value?.id : coin?.chainType === ChainType.WK
      );

      if (currentNetCoin) {
        setSelectedToken(currentNetCoin);
      }
    }
  }, [currentNetCoins, setSelectedToken, value?.id]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setIsMoreOpen(false);
      setSearchValue('');
    }
    setCurrent(initDefaultNet as Net);
    setOpen(isOpen);
  }, [isOpen, searchValue]);
  return (
    <Dialog.Root
      placement="center"
      open={open}
      onOpenChange={(details) => setIsOpen(details.open)}
      unmountOnExit
    >
      <Portal container={portalContainerRef}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content pt="2.5" px="5" pb="5" w="388px" maxW="100%">
            <Dialog.Header
              px="0"
              py="2.5"
              fontSize="md"
              fontWeight="semibold"
              justifyContent="space-between"
            >
              <Flex
                cursor={isMoreOpen ? 'pointer' : 'default'}
                alignItems="center"
                color="fg"
                gap="2"
                onClick={() => {
                  setCurrent(initDefaultNet as Net);
                  setSearchValue('');
                  setIsMoreOpen(false);
                }}
              >
                {isMoreOpen && <BackIcon />}
                <Box>{t('chat.selectCoinText')}</Box>
              </Flex>
              {!isMoreOpen && (
                <Box
                  fontSize="xs"
                  cursor="pointer"
                  onClick={() => {
                    setSearchValue('');
                    setIsMoreOpen(true);
                  }}
                >
                  {t('action.more')}
                </Box>
              )}
            </Dialog.Header>
            <Dialog.Body p="0" py="2">
              <SearchInput value={searchValue} onChange={(value) => setSearchValue(value)} />
              <Box pt="2">
                {isMoreOpen && !searchValue && (
                  <MoreTokensTabs
                    supportNetList={supportNetList}
                    onSelectNet={setCurrent}
                    currentNet={currentNet}
                  />
                )}
                <Tabs.Root
                  className="TokensPanel"
                  flex="1"
                  defaultValue="Token"
                  display="flex"
                  flexDirection="column"
                  h="full"
                  maxH="full"
                  gap="2.5"
                >
                  <FullBox.Root column>
                    <FullBox.Aside>
                      <Tabs.List alignItems="center" justifyContent="space-between">
                        <Flex gap="6">
                          {tabs.map((tab) => {
                            return (
                              <Tabs.Trigger
                                key={tab.value}
                                value={tab.value}
                                px="0"
                                minW="0"
                                h="9"
                                _before={{
                                  w: '3',
                                  bg: 'fg',
                                  margin: '0 auto',
                                  fontWeight: 'semibold',
                                }}
                              >
                                {tab.label}
                              </Tabs.Trigger>
                            );
                          })}
                        </Flex>
                      </Tabs.List>
                    </FullBox.Aside>
                    <FullBox.Main
                      className="TokensContent"
                      overflowY="auto"
                      borderRadius="xl"
                      bg="white"
                      mt="2.5"
                      maxH="300px"
                    >
                      <Tabs.Content value="Token" pt="0">
                        <Box>
                          {currentNetCoins
                            .filter((coin) =>
                              coin.symbol.toLowerCase().includes(searchValue.toLowerCase())
                            )
                            .map((coin) => (
                              <CoinItem
                                key={coin.id}
                                coin={coin as any}
                                coinIconProps={{ showNetIcon: true }}
                                onClick={() => {
                                  setIsOpen?.(false);
                                  setIsMoreOpen(false);
                                  setSelectedToken(coin);
                                }}
                              />
                            ))}
                        </Box>
                      </Tabs.Content>
                    </FullBox.Main>
                  </FullBox.Root>
                </Tabs.Root>
              </Box>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

interface MoreTokensTabsProps {
  currentNet: Net;
  supportNetList: Net[];
  onSelectNet: (net: Net) => void;
}

function MoreTokensTabs({ currentNet, supportNetList, onSelectNet }: MoreTokensTabsProps) {
  const [selectedNet, setSelectedNet] = useState(currentNet.id);
  const [scrollLeft, setScrollLeft] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const maxScrollLeft = () => {
    if (!containerRef.current || !contentRef.current) return 0;
    return contentRef.current.scrollWidth - containerRef.current.clientWidth;
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const scrollAmount = e.deltaY * 0.5;
    const newScrollLeft = Math.max(0, Math.min(scrollLeft + scrollAmount, maxScrollLeft()));

    setScrollLeft(newScrollLeft);
  };

  return (
    <Flex className="MoreTokensTabs" alignItems="center" gap="2">
      <Center
        flexShrink="0"
        w="20px"
        h="20px"
        bg="bg.subtle"
        borderRadius="full"
        cursor="pointer"
        onClick={() => {
          const val = scrollLeft - 100;
          setScrollLeft(Math.max(0, val));
        }}
      >
        <BackIcon />
      </Center>
      <Box
        ref={containerRef}
        className="scrollHover"
        w="full"
        overflowX="hidden"
        onWheel={handleWheel}
      >
        <Flex
          ref={contentRef}
          gap="2"
          flexWrap="nowrap"
          position="relative"
          transform={`translateX(-${scrollLeft}px)`}
          transition="transform 0.2s ease"
        >
          {supportNetList?.map((net) => (
            <Flex
              alignItems="center"
              gap="1"
              key={net.id}
              px="2.5"
              py="2"
              fontSize="xs"
              fontWeight="medium"
              flexShrink="0"
              bg="bg.subtle"
              borderRadius="lg"
              border="1px solid"
              borderColor={net.id === selectedNet ? 'brand.100' : 'transparent'}
              cursor="pointer"
              onClick={() => {
                setSelectedNet(net.id);
                onSelectNet(net);
              }}
            >
              <Image
                src={net.id === initDefaultNet.id ? AllNetworksImg : net.logo}
                borderRadius="full"
                alt=""
                width="16px"
                height="16px"
              />
              <Box>{net.chainName}</Box>
            </Flex>
          ))}
        </Flex>
      </Box>
      <Center
        flexShrink="0"
        w="20px"
        h="20px"
        transform="rotate(180deg)"
        bg="bg.subtle"
        borderRadius="full"
        cursor="pointer"
        onClick={() => {
          const max = maxScrollLeft();
          const newScrollLeft = scrollLeft + 100;
          setScrollLeft(Math.min(newScrollLeft, max));
        }}
      >
        <BackIcon />
      </Center>
    </Flex>
  );
}
